import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { barberDashboardService, BarberStats, PaymentRequest, PaymentResponse } from '../../services/barberDashboardService';
import { barberService } from '../../services/barberService';
import { Barber } from '../../types/barber.types';
import { Appointment } from '../../types/appointment.types';
import Modal from '../../components/common/Modal';
import { useToast } from '../../context/ToastContext';
import WeeklyCalendar from '../../components/barber/WeeklyCalendar';
import DailyCalendar from '../../components/barber/DailyCalendar';
import AppointmentActionModal from '../../components/barber/AppointmentActionModal';
import BarberHeader from '../../components/barber/BarberHeader';
import { getLocalDateString } from '../../utils/dateUtils';

type TabType = 'appointments' | 'pending-payments' | 'history';

const formatDateSafe = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const isDateOnly = !dateStr.includes('T');
    return date.toLocaleDateString('es-AR', isDateOnly ? { timeZone: 'UTC' } : undefined);
};

const BarberDashboard: React.FC = () => {
    const [stats, setStats] = useState<BarberStats | null>(null);
    const [weekAppointments, setWeekAppointments] = useState<Appointment[]>([]);
    const [pendingPayments, setPendingPayments] = useState<PaymentResponse[]>([]);
    const [historyAppointments, setHistoryAppointments] = useState<Appointment[]>([]);
    const [barber, setBarber] = useState<Barber | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
    const [selectedPendingPayment, setSelectedPendingPayment] = useState<PaymentResponse | null>(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showPendingPaymentModal, setShowPendingPaymentModal] = useState(false);
    const [showActionModal, setShowActionModal] = useState(false);
    const [activeTab, setActiveTab] = useState<TabType>('appointments');
    const [calendarView, setCalendarView] = useState<'weekly' | 'daily'>(() => {
        const savedView = localStorage.getItem('barberCalendarView');
        return (savedView as 'weekly' | 'daily') || 'daily';
    });
    const [currentWeekStart, setCurrentWeekStart] = useState<Date | null>(() => {
        const savedDate = sessionStorage.getItem('barberCurrentWeekStart');
        return savedDate ? new Date(savedDate) : null;
    });
    const [currentDailyDay, setCurrentDailyDay] = useState<Date | null>(() => {
        const savedDay = sessionStorage.getItem('barberCurrentDailyDay');
        return savedDay ? new Date(savedDay) : null;
    });
    const [paymentForm, setPaymentForm] = useState<PaymentRequest>({
        appointmentId: 0,
        amount: 0,
        paymentMethod: 'CASH',
        transactionReference: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [receiptImage, setReceiptImage] = useState<File | null>(null);
    const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const { showToast } = useToast();

    const navigate = useNavigate();
    const user = authService.getCurrentUser();

    useEffect(() => {
        localStorage.setItem('barberCalendarView', calendarView);
    }, [calendarView]);

    useEffect(() => {
        if (currentWeekStart) {
            sessionStorage.setItem('barberCurrentWeekStart', currentWeekStart.toISOString());
        }
    }, [currentWeekStart]);

    useEffect(() => {
        if (currentDailyDay) {
            sessionStorage.setItem('barberCurrentDailyDay', currentDailyDay.toISOString());
        }
    }, [currentDailyDay]);

    useEffect(() => {
        loadDashboardData();
        loadWeekAppointments(currentWeekStart || undefined);
    }, []);

    useEffect(() => {
        if (activeTab === 'history' && historyAppointments.length === 0) {
            loadHistoryAppointments();
        }
    }, [activeTab]);

    const loadHistoryAppointments = async () => {
        try {
            setLoadingHistory(true);
            const allApps = await barberDashboardService.getAllAppointments();
            const history = allApps.filter(a => 
                a.status === 'CANCELLED' || 
                (a.notes && (a.notes.includes('[Reprogramado') || a.notes.includes('[Cancelado')))
            );
            history.sort((a, b) => new Date(b.appointmentDate).getTime() - new Date(a.appointmentDate).getTime());
            setHistoryAppointments(history);
        } catch (error) {
            console.error('Error loading history:', error);
            showToast('Error al cargar historial', 'error');
        } finally {
            setLoadingHistory(false);
        }
    };

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            const [statsData, pendingPaymentsData, barberData] = await Promise.all([
                barberDashboardService.getStats(),
                barberDashboardService.getPendingPayments(),
                barberService.getMyStatus()
            ]);
            setStats(statsData);
            setPendingPayments(pendingPaymentsData);
            setBarber(barberData);
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            showToast('Error al cargar datos del dashboard', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadWeekAppointments = async (weekStart?: Date) => {
        try {
            let monday: Date;

            if (weekStart) {
                // Use the provided week start
                monday = new Date(weekStart);
            } else {
                // Get current week's Monday
                const today = new Date();
                const day = today.getDay();
                const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
                monday = new Date(today.setDate(diff));
            }

            // Load appointments for 7 days starting from Monday
            const promises: Promise<Appointment[]>[] = [];
            for (let i = 0; i < 7; i++) {
                const date = new Date(monday);
                date.setDate(monday.getDate() + i);
                const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
                promises.push(barberDashboardService.getAppointmentsByDate(dateStr));
            }

            const results = await Promise.all(promises);
            const allAppointments = results.flat();
            setWeekAppointments(allAppointments);
        } catch (error) {
            console.error('Error loading week appointments:', error);
        }
    };

    const openPendingPaymentModal = (payment: PaymentResponse) => {
        setSelectedPendingPayment(payment);
        setPaymentForm({
            amount: payment.amount,
            paymentMethod: 'CASH',
            transactionReference: ''
        });
        setReceiptImage(null);
        setReceiptPreview(null);
        setShowPendingPaymentModal(true);
    };

    const handleImageCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setReceiptImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setReceiptPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const clearReceiptImage = () => {
        setReceiptImage(null);
        setReceiptPreview(null);
    };

    const requiresReceipt = (method: string) => {
        return method === 'BANK_TRANSFER' || method === 'MERCADO_PAGO';
    };

    const handleRegisterPayment = async () => {
        if (!selectedAppointment) return;

        if (requiresReceipt(paymentForm.paymentMethod) && !receiptImage) {
            showToast('Debe subir una foto del comprobante de transferencia', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            let updatedForm = { ...paymentForm };

            if (receiptImage) {
                setUploadingImage(true);
                const uploadResponse = await barberDashboardService.uploadReceiptImage(receiptImage);
                updatedForm.receiptImageUrl = uploadResponse.url;
                setUploadingImage(false);
            }

            await barberDashboardService.registerPayment(updatedForm);
            setShowPaymentModal(false);
            clearReceiptImage();
            loadWeekAppointments();
            loadDashboardData();
            showToast('Pago registrado exitosamente', 'success');
        } catch (error) {
            console.error('Error registering payment:', error);
            showToast('Error al registrar el pago', 'error');
        } finally {
            setSubmitting(false);
            setUploadingImage(false);
        }
    };

    const handleCompletePendingPayment = async () => {
        if (!selectedPendingPayment) return;

        if (requiresReceipt(paymentForm.paymentMethod) && !receiptImage) {
            showToast('Debe subir una foto del comprobante de transferencia', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            let updatedForm = { ...paymentForm };

            if (receiptImage) {
                setUploadingImage(true);
                const uploadResponse = await barberDashboardService.uploadReceiptImage(receiptImage);
                updatedForm.receiptImageUrl = uploadResponse.url;
                setUploadingImage(false);
            }

            await barberDashboardService.completePendingPayment(selectedPendingPayment.id, updatedForm);
            setShowPendingPaymentModal(false);
            clearReceiptImage();
            loadDashboardData();
            showToast('Pago completado exitosamente', 'success');
        } catch (error) {
            console.error('Error completing pending payment:', error);
            showToast('Error al completar el pago', 'error');
        } finally {
            setSubmitting(false);
            setUploadingImage(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: '1rem' }}>
                <div className="spinner"></div>
                <p>Cargando dashboard...</p>
            </div>
        );
    }

    return (
        <>
            <BarberHeader
                title="✂️ Panel de Barbero"
                subtitle={`Hola, ${user?.firstName || user?.name}! Bienvenido a tu panel`}
                rightContent={
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                            className="btn btn-sm"
                            onClick={() => navigate('/barber/walk-in')}
                            style={{
                                backgroundColor: '#22c55e',
                                color: 'white',
                                border: 'none',
                                fontWeight: '600'
                            }}
                        >
                            🚶 Atender Ahora
                        </button>
                        <button className="btn btn-outline btn-sm" onClick={() => navigate('/barber/profile')}>
                            ⚙️ Perfil
                        </button>
                    </div>
                }
            />
            <div className="container fade-in" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1rem 2rem 1rem' }}>

                {/* Stats Badges - Compact horizontal row */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    padding: '0.75rem',
                    backgroundColor: '#111111',
                    borderRadius: '0.75rem',
                    border: '1px solid #2a2a2a'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: '#3b82f615',
                        borderRadius: '2rem',
                        border: '1px solid #3b82f630'
                    }}>
                        <span style={{ fontSize: '1rem' }}>📅</span>
                        <span style={{ fontWeight: '700', color: '#3b82f6', fontSize: '1.1rem' }}>{stats?.todayAppointments || 0}</span>
                        <span style={{ color: '#525252', fontSize: '0.8rem' }}>Turnos Hoy</span>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: '#f59e0b15',
                        borderRadius: '2rem',
                        border: '1px solid #f59e0b30'
                    }}>
                        <span style={{ fontSize: '1rem' }}>⏳</span>
                        <span style={{ fontWeight: '700', color: '#f59e0b', fontSize: '1.1rem' }}>{stats?.pendingToday || 0}</span>
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>Pendientes</span>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: '#10b98115',
                        borderRadius: '2rem',
                        border: '1px solid #10b98130'
                    }}>
                        <span style={{ fontSize: '1rem' }}>✅</span>
                        <span style={{ fontWeight: '700', color: '#10b981', fontSize: '1.1rem' }}>{stats?.completedToday || 0}</span>
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>Completados</span>
                    </div>

                    <div
                        onClick={() => stats?.pendingPayments && setActiveTab('pending-payments')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 1rem',
                            backgroundColor: stats?.pendingPayments ? '#f59e0b15' : '#ef444415',
                            borderRadius: '2rem',
                            border: stats?.pendingPayments ? '2px solid #f59e0b50' : '1px solid #ef444430',
                            cursor: stats?.pendingPayments ? 'pointer' : 'default',
                            transition: 'all 0.2s'
                        }}
                    >
                        <span style={{ fontSize: '1rem' }}>💳</span>
                        <span style={{ fontWeight: '700', color: stats?.pendingPayments ? '#d97706' : '#ef4444', fontSize: '1.1rem' }}>
                            {stats?.pendingPayments || 0}
                        </span>
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>Saldos</span>
                        {stats?.pendingPayments ? <span style={{ fontSize: '0.75rem' }}>⚠️</span> : null}
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-lg">
                    <div style={{ borderBottom: '2px solid #2a2a2a', display: 'flex', gap: '2rem' }}>
                        <button
                            onClick={() => setActiveTab('appointments')}
                            style={{
                                padding: '0.75rem 1rem',
                                border: 'none',
                                background: 'none',
                                fontWeight: '600',
                                borderBottom: activeTab === 'appointments' ? '3px solid #f59e0b' : '3px solid transparent',
                                color: activeTab === 'appointments' ? '#f59e0b' : '#525252',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                marginBottom: '-2px'
                            }}
                        >
                            📋 Turnos del Día
                        </button>
                        <button
                            onClick={() => setActiveTab('pending-payments')}
                            style={{
                                padding: '0.75rem 1rem',
                                border: 'none',
                                background: 'none',
                                fontWeight: '600',
                                borderBottom: activeTab === 'pending-payments' ? '3px solid #f59e0b' : '3px solid transparent',
                                color: activeTab === 'pending-payments' ? '#f59e0b' : '#525252',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                marginBottom: '-2px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            💳 Saldos Pendientes
                            {pendingPayments.length > 0 && (
                                <span style={{
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    borderRadius: '9999px',
                                    padding: '0.125rem 0.5rem',
                                    fontSize: '0.75rem',
                                    fontWeight: '700'
                                }}>
                                    {pendingPayments.length}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            style={{
                                padding: '0.75rem 1rem',
                                border: 'none',
                                background: 'none',
                                fontWeight: '600',
                                borderBottom: activeTab === 'history' ? '3px solid #f59e0b' : '3px solid transparent',
                                color: activeTab === 'history' ? '#f59e0b' : '#525252',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                marginBottom: '-2px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            🕒 Historial (Movidos/Cancelados)
                        </button>
                    </div>
                </div>

                {/* Appointments Tab */}
                {activeTab === 'appointments' && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', backgroundColor: '#1a1a1a', borderRadius: '0.5rem', padding: '0.25rem', border: '1px solid #333' }}>
                                <button
                                    onClick={() => setCalendarView('daily')}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        backgroundColor: calendarView === 'daily' ? '#2a2a2a' : 'transparent',
                                        color: calendarView === 'daily' ? '#f59e0b' : '#a3a3a3',
                                        border: 'none',
                                        borderRadius: '0.25rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    Día
                                </button>
                                <button
                                    onClick={() => setCalendarView('weekly')}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        backgroundColor: calendarView === 'weekly' ? '#2a2a2a' : 'transparent',
                                        color: calendarView === 'weekly' ? '#f59e0b' : '#a3a3a3',
                                        border: 'none',
                                        borderRadius: '0.25rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    Semana
                                </button>
                            </div>
                        </div>

                        {calendarView === 'weekly' ? (
                            <WeeklyCalendar
                                appointments={weekAppointments}
                                barber={barber}
                                initialWeekStart={currentWeekStart}
                                onAppointmentClick={(appointment) => {
                                    setSelectedAppointment(appointment);
                                    setShowActionModal(true);
                                }}
                                onSlotClick={(date, time) => {
                                    navigate('/barber/walk-in', {
                                        state: {
                                            startDate: getLocalDateString(date),
                                            startTime: time
                                        }
                                    });
                                }}
                                onWeekChange={(weekStart) => {
                                    setCurrentWeekStart(weekStart);
                                    loadWeekAppointments(weekStart);
                                }}
                            />
                        ) : (
                            <DailyCalendar
                                appointments={weekAppointments}
                                barber={barber}
                                initialDay={currentDailyDay || currentWeekStart || null}
                                onAppointmentClick={(appointment) => {
                                    setSelectedAppointment(appointment);
                                    setShowActionModal(true);
                                }}
                                onSlotClick={(date, time) => {
                                    navigate('/barber/walk-in', {
                                        state: {
                                            startDate: getLocalDateString(date),
                                            startTime: time
                                        }
                                    });
                                }}
                                onDayChange={(day) => {
                                    setCurrentDailyDay(day);
                                    const d = new Date(day);
                                    const dayOfWeek = d.getDay();
                                    const diff = d.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
                                    const weekStart = new Date(d.setDate(diff));
                                    
                                    if (!currentWeekStart || currentWeekStart.getTime() !== weekStart.getTime()) {
                                        setCurrentWeekStart(weekStart);
                                        loadWeekAppointments(weekStart);
                                    }
                                }}
                            />
                        )}
                    </>
                )}

                {/* Pending Payments Tab */}
                {activeTab === 'pending-payments' && (
                    <>
                        <h2 className="text-xl font-bold mb-md" style={{ color: '#f59e0b' }}>💳 Saldos Pendientes de Cobro</h2>

                        {pendingPayments.length === 0 ? (
                            <div className="card text-center p-xl" style={{ background: '#111111', border: '1px solid #2a2a2a', borderRadius: '1rem' }}>
                                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
                                <h4 className="mb-sm" style={{ color: '#e5e5e5' }}>No hay saldos pendientes</h4>
                                <p style={{ color: '#737373' }}>Todos los pagos están al día</p>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gap: '1rem' }}>
                                {pendingPayments.map((payment) => (
                                    <div
                                        key={payment.id}
                                        className="card hover:shadow-md transition-all"
                                        style={{
                                            background: '#111111',
                                            borderRadius: '1rem',
                                            border: '1px solid #f59e0b30'
                                        }}
                                    >
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '1.5rem', alignItems: 'center' }}>
                                            {/* Info */}
                                            <div>
                                                <h4 className="font-bold mb-xs" style={{ color: '#e5e5e5' }}>{payment.clientName}</h4>
                                                <p className="text-sm mb-xs" style={{ color: '#737373' }}>{payment.serviceDescription || 'Corte de Pelo'}</p>
                                                <p className="text-sm" style={{ color: '#525252' }}>
                                                    📅 {formatDateSafe(payment.appointmentDate || payment.createdAt)}
                                                </p>
                                            </div>

                                            {/* Amount */}
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#d97706' }}>
                                                    ${payment.amount}
                                                </div>
                                                <span style={{
                                                    backgroundColor: '#fbbf2420',
                                                    color: '#d97706',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '9999px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600'
                                                }}>
                                                    Pendiente
                                                </span>
                                            </div>

                                            {/* Action */}
                                            <div>
                                                <button
                                                    className="btn btn-sm"
                                                    onClick={() => openPendingPaymentModal(payment)}
                                                    style={{ backgroundColor: '#10b981', color: 'white' }}
                                                >
                                                    💰 Cobrar Ahora
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* History Tab */}
                {activeTab === 'history' && (
                    <>
                        <h2 className="text-xl font-bold mb-md" style={{ color: '#f59e0b' }}>🕒 Historial de Turnos Movidos/Cancelados</h2>

                        {loadingHistory ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                                <div className="spinner"></div>
                            </div>
                        ) : historyAppointments.length === 0 ? (
                            <div className="card text-center p-xl" style={{ background: '#111111', border: '1px solid #2a2a2a', borderRadius: '1rem' }}>
                                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📋</div>
                                <h4 className="mb-sm" style={{ color: '#e5e5e5' }}>No hay historial</h4>
                                <p style={{ color: '#737373' }}>No tenés turnos cancelados ni reprogramados</p>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gap: '1rem' }}>
                                {historyAppointments.map((appointment) => {
                                    const isCancelled = appointment.status === 'CANCELLED';
                                    return (
                                        <div
                                            key={appointment.id}
                                            className="card"
                                            style={{
                                                background: '#111111',
                                                borderRadius: '1rem',
                                                border: isCancelled ? '1px solid #ef444430' : '1px solid #f59e0b30'
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <div>
                                                        <h4 className="font-bold mb-xs" style={{ color: '#e5e5e5' }}>{appointment.clientName}</h4>
                                                        <p className="text-sm mb-xs" style={{ color: '#737373' }}>{appointment.serviceDescription || 'Corte de Pelo'}</p>
                                                        <p className="text-sm" style={{ color: '#525252' }}>
                                                            📅 {formatDateSafe(appointment.appointmentDate)} - {appointment.startTime?.substring(0, 5)}
                                                        </p>
                                                    </div>
                                                    <span style={{
                                                        backgroundColor: isCancelled ? '#ef444420' : '#f59e0b20',
                                                        color: isCancelled ? '#ef4444' : '#f59e0b',
                                                        padding: '0.25rem 0.75rem',
                                                        borderRadius: '9999px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: '600'
                                                    }}>
                                                        {isCancelled ? 'Cancelado' : 'Reprogramado'}
                                                    </span>
                                                </div>
                                                {appointment.notes && (
                                                    <div style={{
                                                        backgroundColor: '#1a1a1a',
                                                        padding: '0.75rem',
                                                        borderRadius: '0.5rem',
                                                        fontSize: '0.875rem',
                                                        color: '#a3a3a3',
                                                        borderLeft: `4px solid ${isCancelled ? '#ef4444' : '#f59e0b'}`
                                                    }}>
                                                        <p style={{ margin: 0, whiteSpace: 'pre-line' }}>{appointment.notes}</p>
                                                    </div>
                                                )}
                                                {appointment.clientPhone && (
                                                    <div style={{ marginTop: '0.25rem' }}>
                                                        <a 
                                                            href={`https://wa.me/${appointment.clientPhone}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ 
                                                                display: 'inline-flex', 
                                                                alignItems: 'center', 
                                                                gap: '0.35rem', 
                                                                color: '#25D366', 
                                                                textDecoration: 'none', 
                                                                fontSize: '0.85rem',
                                                                fontWeight: '600',
                                                                backgroundColor: '#25D36620',
                                                                padding: '0.35rem 0.75rem',
                                                                borderRadius: '0.5rem',
                                                                width: 'fit-content',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#25D36630';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#25D36620';
                                                            }}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                                <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232"/>
                                                            </svg>
                                                            Contactar ({appointment.clientPhone})
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {/* Payment Modal - New Payment */}
                <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="Registrar Pago">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label className="form-label">Cliente</label>
                            <input
                                type="text"
                                className="form-input"
                                value={selectedAppointment?.clientName || ''}
                                disabled
                            />
                        </div>
                        <div>
                            <label className="form-label">Monto</label>
                            <input
                                type="number"
                                className="form-input"
                                value={paymentForm.amount}
                                onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) })}
                            />
                        </div>
                        <div>
                            <label className="form-label">Método de Pago</label>
                            <select
                                className="form-input"
                                value={paymentForm.paymentMethod}
                                onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value as PaymentRequest['paymentMethod'] })}
                            >
                                <option value="CASH">💵 Efectivo</option>
                                <option value="BANK_TRANSFER">🏦 Transferencia</option>
                                <option value="MERCADO_PAGO">📱 Mercado Pago</option>
                                <option value="DEBIT_CARD">💳 Débito</option>
                                <option value="CREDIT_CARD">💳 Crédito</option>
                            </select>
                        </div>
                        {paymentForm.paymentMethod !== 'CASH' && (
                            <div>
                                <label className="form-label">Referencia de Transacción</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Número de operación"
                                    value={paymentForm.transactionReference}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, transactionReference: e.target.value })}
                                />
                            </div>
                        )}
                        {requiresReceipt(paymentForm.paymentMethod) && (
                            <div>
                                <label className="form-label">📷 Foto del Comprobante *</label>
                                {receiptPreview ? (
                                    <div style={{ position: 'relative' }}>
                                        {receiptImage?.type === 'application/pdf' ? (
                                            <div style={{ width: '100%', padding: '2rem', backgroundColor: '#2a2a2a', borderRadius: '0.5rem', marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid #404040' }}>
                                                <span style={{ fontSize: '3rem' }}>📄</span>
                                                <span style={{ color: '#e5e5e5', marginTop: '0.5rem', fontSize: '0.875rem', wordBreak: 'break-all', textAlign: 'center' }}>{receiptImage.name}</span>
                                            </div>
                                        ) : (
                                            <img src={receiptPreview} alt="Comprobante" style={{ width: '100%', borderRadius: '0.5rem', marginBottom: '0.5rem' }} />
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline"
                                            onClick={clearReceiptImage}
                                        >
                                            ✕ Quitar
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <input
                                                type="file"
                                                id="receipt-image-camera"
                                                accept="image/*"
                                                capture="environment"
                                                onChange={handleImageCapture}
                                                style={{ display: 'none' }}
                                            />
                                            <label
                                                htmlFor="receipt-image-camera"
                                                className="btn btn-outline"
                                                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', cursor: 'pointer', padding: '0.75rem 0.25rem' }}
                                            >
                                                <span style={{ fontSize: '1.25rem' }}>📸</span>
                                                <span style={{ fontSize: '0.75rem', textAlign: 'center' }}>Tomar Foto</span>
                                            </label>

                                            <input
                                                type="file"
                                                id="receipt-image-gallery"
                                                accept="image/*,application/pdf"
                                                onChange={handleImageCapture}
                                                style={{ display: 'none' }}
                                            />
                                            <label
                                                htmlFor="receipt-image-gallery"
                                                className="btn btn-outline"
                                                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', cursor: 'pointer', padding: '0.75rem 0.25rem' }}
                                            >
                                                <span style={{ fontSize: '1.25rem' }}>🖼️</span>
                                                <span style={{ fontSize: '0.75rem', textAlign: 'center' }}>Galería/Archivo</span>
                                            </label>
                                        </div>
                                    </div>
                                )}
                                <p className="text-sm text-secondary mt-xs">Requerido para transferencias</p>
                            </div>
                        )}
                        <div className="flex justify-end gap-sm mt-lg">
                            <button className="btn btn-outline" onClick={() => setShowPaymentModal(false)} disabled={submitting || uploadingImage}>
                                Cancelar
                            </button>
                            <button
                                className="btn"
                                onClick={handleRegisterPayment}
                                disabled={submitting || uploadingImage}
                                style={{ backgroundColor: '#10b981', color: 'white' }}
                            >
                                {uploadingImage ? 'Subiendo imagen...' : submitting ? 'Procesando...' : 'Confirmar Pago'}
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Pending Payment Modal */}
                <Modal isOpen={showPendingPaymentModal} onClose={() => setShowPendingPaymentModal(false)} title="Cobrar Saldo Pendiente">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label className="form-label">Cliente</label>
                            <input
                                type="text"
                                className="form-input"
                                value={selectedPendingPayment?.clientName || ''}
                                disabled
                            />
                        </div>
                        <div>
                            <label className="form-label">Servicio</label>
                            <input
                                type="text"
                                className="form-input"
                                value={selectedPendingPayment?.serviceDescription || 'Corte de Pelo'}
                                disabled
                            />
                        </div>
                        <div>
                            <label className="form-label">Fecha del Turno</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formatDateSafe(selectedPendingPayment?.appointmentDate)}
                                disabled
                            />
                        </div>
                        <div>
                            <label className="form-label">Monto</label>
                            <input
                                type="number"
                                className="form-input"
                                value={paymentForm.amount}
                                onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) })}
                            />
                        </div>
                        <div>
                            <label className="form-label">Método de Pago</label>
                            <select
                                className="form-input"
                                value={paymentForm.paymentMethod}
                                onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value as PaymentRequest['paymentMethod'] })}
                            >
                                <option value="CASH">💵 Efectivo</option>
                                <option value="BANK_TRANSFER">🏦 Transferencia</option>
                                <option value="MERCADO_PAGO">📱 Mercado Pago</option>
                                <option value="DEBIT_CARD">💳 Débito</option>
                                <option value="CREDIT_CARD">💳 Crédito</option>
                            </select>
                        </div>
                        {paymentForm.paymentMethod !== 'CASH' && (
                            <div>
                                <label className="form-label">Referencia de Transacción</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Número de operación"
                                    value={paymentForm.transactionReference}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, transactionReference: e.target.value })}
                                />
                            </div>
                        )}
                        {requiresReceipt(paymentForm.paymentMethod) && (
                            <div>
                                <label className="form-label">📷 Foto del Comprobante *</label>
                                {receiptPreview ? (
                                    <div style={{ position: 'relative' }}>
                                        {receiptImage?.type === 'application/pdf' ? (
                                            <div style={{ width: '100%', padding: '2rem', backgroundColor: '#2a2a2a', borderRadius: '0.5rem', marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid #404040' }}>
                                                <span style={{ fontSize: '3rem' }}>📄</span>
                                                <span style={{ color: '#e5e5e5', marginTop: '0.5rem', fontSize: '0.875rem', wordBreak: 'break-all', textAlign: 'center' }}>{receiptImage.name}</span>
                                            </div>
                                        ) : (
                                            <img src={receiptPreview} alt="Comprobante" style={{ width: '100%', borderRadius: '0.5rem', marginBottom: '0.5rem' }} />
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline"
                                            onClick={clearReceiptImage}
                                        >
                                            ✕ Quitar
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <input
                                                type="file"
                                                id="receipt-image-pending-camera"
                                                accept="image/*"
                                                capture="environment"
                                                onChange={handleImageCapture}
                                                style={{ display: 'none' }}
                                            />
                                            <label
                                                htmlFor="receipt-image-pending-camera"
                                                className="btn btn-outline"
                                                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', cursor: 'pointer', padding: '0.75rem 0.25rem' }}
                                            >
                                                <span style={{ fontSize: '1.25rem' }}>📸</span>
                                                <span style={{ fontSize: '0.75rem', textAlign: 'center' }}>Tomar Foto</span>
                                            </label>

                                            <input
                                                type="file"
                                                id="receipt-image-pending-gallery"
                                                accept="image/*,application/pdf"
                                                onChange={handleImageCapture}
                                                style={{ display: 'none' }}
                                            />
                                            <label
                                                htmlFor="receipt-image-pending-gallery"
                                                className="btn btn-outline"
                                                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', cursor: 'pointer', padding: '0.75rem 0.25rem' }}
                                            >
                                                <span style={{ fontSize: '1.25rem' }}>🖼️</span>
                                                <span style={{ fontSize: '0.75rem', textAlign: 'center' }}>Galería/Archivo</span>
                                            </label>
                                        </div>
                                    </div>
                                )}
                                <p className="text-sm text-secondary mt-xs">Requerido para transferencias</p>
                            </div>
                        )}
                        <div className="flex justify-end gap-sm mt-lg">
                            <button className="btn btn-outline" onClick={() => setShowPendingPaymentModal(false)} disabled={submitting || uploadingImage}>
                                Cancelar
                            </button>
                            <button
                                className="btn"
                                onClick={handleCompletePendingPayment}
                                disabled={submitting || uploadingImage}
                                style={{ backgroundColor: '#10b981', color: 'white' }}
                            >
                                {uploadingImage ? 'Subiendo imagen...' : submitting ? 'Procesando...' : '✓ Cobrar y Completar'}
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Appointment Action Modal */}
                <AppointmentActionModal
                    appointment={selectedAppointment}
                    isOpen={showActionModal}
                    onClose={() => {
                        setShowActionModal(false);
                        setSelectedAppointment(null);
                    }}
                    onAppointmentUpdated={() => {
                        loadWeekAppointments(currentWeekStart || undefined);
                        loadDashboardData();
                    }}
                    pricePerService={stats?.pricePerService || 0}
                />
            </div>
        </>
    );
};

export default BarberDashboard;
